import React from 'react';
import { FiDisplayMessage, useFi } from '../../contexts/fi-context';
import { FiToolResult } from '../../hooks/api/useFiChat';
import { FiCrisisMessage } from './fi-crisis-message';
import { FiClarifyMessage } from './fi-clarify-message';
import { FiFaqMessage } from './fi-faq-message';
import { FiStoryResults } from './fi-story-results';
import { FiListenerResults } from './fi-listener-results';
import { FiPeersonaResults } from './fi-peersona-results';
import {
  FI_ASSISTANT_BUBBLE_RADII,
  FI_BUBBLE_ASSISTANT_BG,
  FI_BUBBLE_ASSISTANT_TEXT,
  FI_BUBBLE_USER_BG,
  FI_BUBBLE_USER_TEXT,
  FI_TEXT_MUTED,
  FI_USER_BUBBLE_RADII,
} from './fi-styles';
import { formatCompactRelativeTime } from '../../utils/relative-time';

interface FiMessageBubbleProps {
  message: FiDisplayMessage;
  index: number;
}

function toolItems(tool: FiToolResult): Record<string, unknown>[] {
  const r = tool.result;
  const items = r?.items;
  return Array.isArray(items) ? (items as Record<string, unknown>[]) : [];
}

function toolQuery(tool: FiToolResult): string {
  const q = tool.result?.query;
  return typeof q === 'string' ? q : '';
}

function toolString(tool: FiToolResult, field: string): string {
  const v = tool.result?.[field];
  return typeof v === 'string' ? v : '';
}

/** Point legacy backend copy at the sticky resources strip. */
function normalizeResourcesPointer(text: string): string {
  if (!text.trim()) return text;
  return text
    .replace(/view organizational resources above/gi, 'View organizational resources above')
    .replace(/resources in the header/gi, 'View organizational resources above')
    .replace(/organizational resources above/gi, 'View organizational resources above');
}

/** Assistant prose text in the chat shell. Tool-result components sit between ScriptBubbles. */
const ScriptBubble: React.FC<{ text: string }> = ({ text }) =>
  text ? (
    <div className={`${FI_ASSISTANT_BUBBLE_RADII} px-5 py-3.5`} style={{ backgroundColor: FI_BUBBLE_ASSISTANT_BG }}>
      <p
        className="font-open-sans whitespace-pre-wrap break-words text-sm leading-5"
        style={{ color: FI_BUBBLE_ASSISTANT_TEXT }}
      >
        {text}
      </p>
    </div>
  ) : null;

/** Left-aligned assistant row with a compact "Fi・Concierge・{time}" meta line. */
const FiMessageRow: React.FC<{ children: React.ReactNode; timestamp?: string }> = ({ children, timestamp }) => {
  const time = formatCompactRelativeTime({ iso: timestamp });
  return (
    <div className="flex justify-start pl-4">
      <div className="min-w-0 max-w-[85%] space-y-1">
        {children}
        <p className="font-albert-sans text-xs leading-5" style={{ color: FI_TEXT_MUTED }}>
          Fi
          <span aria-hidden>・</span>
          Concierge
          {time ? (
            <>
              <span aria-hidden>・</span>
              {time}
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
};

export const FiMessageBubble: React.FC<FiMessageBubbleProps> = ({ message, index }) => {
  const { dismissCrisisMessage, onStoryEngagedFromFi } = useFi();

  if (message.role === 'user') {
    return (
      <div className="flex justify-end pr-4">
        <div
          className={`max-w-[85%] ${FI_USER_BUBBLE_RADII} px-5 py-3.5`}
          style={{ backgroundColor: FI_BUBBLE_USER_BG }}
        >
          <p
            className="font-open-sans whitespace-pre-wrap break-words text-sm leading-5"
            style={{ color: FI_BUBBLE_USER_TEXT }}
          >
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  if (message.fi_kind === 'crisis' && message.crisis && !message.crisis_dismissed) {
    return (
      <FiMessageRow timestamp={message.timestamp}>
        <div className="max-w-[95%]">
          <FiCrisisMessage crisis={message.crisis} onDismiss={() => dismissCrisisMessage(index)} />
        </div>
      </FiMessageRow>
    );
  }

  if (message.fi_kind === 'crisis' && message.crisis_dismissed) {
    return null;
  }

  const tools = message.tool_results || [];
  let clarifyRendered = false;

  return (
    <FiMessageRow timestamp={message.timestamp}>
      <div className="space-y-2">
        {message.content ? <ScriptBubble text={message.content} /> : null}
        {tools.map((tool, ti) => {
          const key = `${index}-${ti}-${tool.tool}`;
          if (tool.tool === 'clarify') {
            const msg = toolString(tool, 'message');
            const followup = toolString(tool, 'followup');
            if (msg) {
              if (clarifyRendered) return null;
              clarifyRendered = true;
              return (
                <React.Fragment key={key}>
                  <FiClarifyMessage text={msg} />
                  <ScriptBubble text={followup} />
                </React.Fragment>
              );
            }
            return null;
          }
          if (tool.tool === 'faq') {
            const msg = toolString(tool, 'message');
            const topic = toolString(tool, 'topic');
            const followup = toolString(tool, 'followup');
            if (msg) {
              return <FiFaqMessage key={key} topic={topic} message={msg} followup={followup} />;
            }
            return null;
          }
          const items = toolItems(tool);
          const q = toolQuery(tool);
          const foundMessage = toolString(tool, 'message');
          const followup = toolString(tool, 'followup');
          if (tool.tool === 'explore_content' && items.length) {
            return (
              <React.Fragment key={key}>
                <ScriptBubble text={foundMessage} />
                <FiStoryResults
                  items={items}
                  searchQuery={q}
                  onEngage={(id) => {
                    onStoryEngagedFromFi(id);
                  }}
                />
                <ScriptBubble text={followup} />
              </React.Fragment>
            );
          }
          if (tool.tool === 'search_listeners' && items.length) {
            return (
              <React.Fragment key={key}>
                <ScriptBubble text={foundMessage} />
                <FiListenerResults items={items} searchQuery={q} />
                <ScriptBubble text={followup} />
              </React.Fragment>
            );
          }
          // KINDLY-1117: resources live in the shared organizational drawer
          // (auto-opened by ``useFiResourcesPanel``), not an in-chat carousel.
          if (tool.tool === 'navigate_resources' && items.length) {
            return (
              <React.Fragment key={key}>
                <ScriptBubble text={normalizeResourcesPointer(foundMessage)} />
                <ScriptBubble text={followup} />
              </React.Fragment>
            );
          }
          if (tool.tool === 'find_peersonas' && items.length) {
            return (
              <React.Fragment key={key}>
                <ScriptBubble text={foundMessage} />
                <FiPeersonaResults items={items} searchQuery={q} />
                <ScriptBubble text={followup} />
              </React.Fragment>
            );
          }
          if (tool.tool === 'closure_reply') {
            const followupText = toolString(tool, 'followup');
            return followupText ? <ScriptBubble key={key} text={followupText} /> : null;
          }
          return null;
        })}
      </div>
    </FiMessageRow>
  );
};
